import React, { useMemo } from 'react';
import { RocketOutlined } from '@ant-design/icons';
import { Outlet, useNavigate } from 'react-router';
import styled from '@emotion/styled';
import Table from 'app/components/Table';
import { getColumnDefs } from './constants';
import { TablePaginationConfig, notification } from 'antd';
import { FilterValue } from 'antd/lib/table/interface';
import {
  useActiveTemplateMutation,
  useInActiveTemplateMutation,
  useSetDefaultTemplateMutation
} from 'app/features/templates/api';
import { TemplateInformation } from './views';
import { get, isEqual, pick, set } from 'lodash';
import TemplateProvider, { TemplateContext } from './context';
import ListLayout from 'app/components/Layout/ListLayout';
import { ReactComponent as TemplateManagement } from 'assets/icons/navs/nav-1.svg';
import Icon from '@ant-design/icons';
import FilterBar, { EFilterItemType } from 'app/components/FilterBar';
import { ReactComponent as SortIcon } from 'assets/icons/toolbars/sort-icon.svg';
import moment from 'moment';
import { useCustomFilter } from 'app/hooks/useCustomFilter';
import { transformSorterAndFilters } from './ultils';

const Container = styled.div`
  min-height: 100%;
  text-align: left;
  background: none;
`;
const WrapperTable = styled.div`
  margin-top: 16px;
  border-radius: 4px;
  box-shadow: 0px 1px 6px 0px #a5b2c833;
  box-shadow: 0px 2px 4px 1px #a5b2c81a;
  background: #fff;
  padding: 24px;
  .ant-table-expanded-row {
    > td {
      background: rgba(250, 252, 255, 1);
    }
  }
`;

const StyledTotalLabel = styled.p`
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.005em;
  text-align: left;
  color: #8991a0;
`;

const StyledIcon = styled(Icon)`
  font-size: 24px;
  path {
    fill: #000;
  }
`;

const TemplateManagementPage = () => {
  return (
    <TemplateProvider>
      <Container>
        <Outlet></Outlet>
      </Container>
    </TemplateProvider>
  );
};
const Index = () => {
  const navigate = useNavigate();
  const { fetchList, list } = React.useContext(TemplateContext);
  const {
    pagination,
    setPagination,
    filters,
    setFilters,
    sorters,
    setSorters,
    searchKeyword,
    setSearchKeyword
  } = useCustomFilter({
    resource: 'template-management',
    syncWithStore: true,
    pagination: {
      current: 1,
      pageSize: 10
    },
    filters: {
      initial: [
        {
          field: 'status',
          operator: 'eq',
          value: []
        },
        {
          field: 'ranges',
          operator: 'between',
          value: undefined
        }
      ]
    },
    sorters: {
      initial: [
        {
          field: 'createdAt',
          order: 'asc'
        }
      ]
    }
  });
  const [active] = useActiveTemplateMutation();
  const [inActive] = useInActiveTemplateMutation();
  const [setDefaultTemplate] = useSetDefaultTemplateMutation();

  const templates = React.useMemo(() => {
    const { data } = list;
    return data;
  }, [list]);

  const onDetail = (id: number) => {
    navigate(`template/${id}`);
  };

  const onDuplicate = (id: number) => {
    navigate(`template?duplicate=${id}`);
  };

  const activeTemplate = async (id: string) => {
    try {
      const response: any = await active(id);
      if (response.error) {
        const message = get(response.error.data, 'msg.message');
        notification.error({ message, placement: 'bottomLeft' });
        return;
      }
      notification.success({
        message: 'Template activated!',
        placement: 'bottomLeft'
      });
      fetchList &&
        fetchList({
          ...(list.originalArgs as any)
        });
    } catch (error) {
      console.log('Active-Template-Error', error);
    }
  };

  const inActiveTemplate = async (id: string) => {
    try {
      const response: any = await inActive(id);
      if (response.error) {
        const message = get(response.error.data, 'msg.message');
        notification.error({ message, placement: 'bottomLeft' });
        return;
      }
      notification.success({
        message: 'Template inactivated!',
        placement: 'bottomLeft'
      });
      fetchList &&
        fetchList({
          ...(list.originalArgs as any)
        });
    } catch (error) {
      console.log('Active-Template-Error', error);
    }
  };

  const setDefault = async (id: string) => {
    try {
      await setDefaultTemplate(id);
      notification.success({
        message: 'Template set as default!',
        placement: 'bottomLeft'
      });
      fetchList &&
        fetchList({
          ...(list.originalArgs as any)
        });
    } catch (error) {
      console.error('Set-Default-Template-Error', error);
    }
  };

  const templateColumns = getColumnDefs(
    onDetail,
    onDuplicate,
    activeTemplate,
    inActiveTemplate,
    setDefault
  );

  const onAddTemplate = () => {
    navigate('template');
  };

  const onTableChange = (pagination: TablePaginationConfig) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize
    });
  };

  const handleChangeFilter = (filter, sort) => {
    const nextFilters = Object.keys(filter || {}).reduce((acc: any, key) => {
      if (filter[key]) {
        acc.push({
          field: key,
          value: filter[key]
        });
      }
      return acc;
    }, []);

    const nextSort = Object.keys(sort || {}).reduce((acc: any, key) => {
      const { sortBy, direction, ranges } = sort[key];
      if (sortBy && direction) {
        acc.push({
          field: sortBy,
          order: direction
        });
      }
      if (Array.isArray(ranges) && ranges.length > 1) {
        nextFilters.push({
          field: sortBy,
          operator: 'between',
          value: ranges.map((dateMoment) => {
            return dateMoment?.format('YYYY-MM-DD');
          })
        });
      }
      return acc;
    }, []);

    if (!isEqual(filters, nextFilters)) {
      setFilters(nextFilters, {
        ...pagination,
        current: 1
      });
    }
    if (!isEqual(sorters, nextSort)) {
      setSorters(nextSort);
    }
  };

  const handleChangeKeyword = (keyword: string) => {
    setSearchKeyword(keyword, {
      current: 1
    });
  };

  const filtered = useMemo(() => {
    // Initialize with default values
    let converted: any = { createdAt: { direction: null, sortBy: null, ranges: [] } };
  
    // Process sorters if they exist
    if (sorters && Array.isArray(sorters) && sorters.length > 0) {
      const sorter = sorters[0]; // Usually only one sorter is applied at a time
      converted.createdAt.direction = sorter.order;
      converted.createdAt.sortBy = sorter.field;
    }
  
    // Process filters if they exist
    if (filters && Array.isArray(filters)) {
      filters.forEach((filter) => {
        // Skip if filter doesn't have a field property
        if (!('field' in filter)) return;
  
        if (filter.operator === 'between') {
          // Handle date range filtering
          if (Array.isArray(filter.value) && filter.value.length > 1) {
            converted = {
              ...converted,
              createdAt: {
                ...converted.createdAt,
                ranges: filter.value.map((stringDate) =>
                  moment(stringDate, 'YYYY-MM-DD')
                )
              }
            };
          }
        } else {
          // Handle regular filtering
          converted = {
            ...converted,
            [filter.field]: filter.value
          };
        }
      });
    }
  
    return converted;
  }, [filters, sorters]);

  React.useEffect(() => {
    if (fetchList) {
      const { filters: transformedFilters, sorters: transformedSorters } =
        transformSorterAndFilters(filters, sorters);
      const params = {
        excludes: ['scheme'],
        ...pagination,
        ...transformedFilters,
        ...transformedSorters,
        keywords: searchKeyword
      };
      if (!searchKeyword) {
        delete params['keywords'];
      }
      fetchList(params as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, filters, sorters, searchKeyword]);

  return (
    <ListLayout
      icon={
        <StyledIcon style={{ fontSize: 24 }} component={TemplateManagement} />
      }
      createButtonText="Create New Template"
      title="Template Management"
      keyword={searchKeyword}
      searchInputProps={{
        placeholder: 'Search template no. , template Name',
        defaultValue: searchKeyword
      }}
      onCreate={onAddTemplate}
      onSearch={handleChangeKeyword}
    >
      <FilterBar
        value={filtered as any}
        defaultValue={{
          createdAt: {
            direction: 'asc',
            sortBy: 'createdAt',
            ranges: []
          },
          status: []
        }}
        onChange={handleChangeFilter}
        filterItems={[
          {
            key: 'createdAt',
            type: EFilterItemType.SORT_DATE,
            label: (_: any, sortBy: any) => {
              if (!sortBy) return 'Sort By Create Date';
              if (sortBy === 'createdAt') return `Sort By Create Date`;
              return `Sort By Latest Update Date`;
            },
            icon: (
              <Icon
                style={{ fontSize: 18, verticalAlign: 'middle' }}
                component={SortIcon}
              ></Icon>
            ),
            sortByOptions: [
              {
                value: 'createdAt',
                label: 'Create Date'
              },
              {
                value: 'updatedAt',
                label: 'Latest Update Date'
              }
            ],
            options: [
              {
                value: 'desc',
                label: 'Newest to Oldest'
              },
              {
                value: 'asc',
                label: 'Oldest to Newest'
              }
            ]
          },
          {
            key: 'status',
            type: EFilterItemType.DROPDOWN,
            icon: <RocketOutlined />,
            label: (selected) => {
              if (selected.includes('all')) {
                return 'All Status';
              }
              return `Status (${selected.length})`;
            },
            options: [
              {
                value: 'active',
                label: 'Active'
              },
              {
                value: 'inactive',
                label: 'Inactive'
              }
            ],
            haveOptionAll: true
          }
        ]}
      ></FilterBar>
      <WrapperTable>
        <Table
          tableLayout="fixed"
          loading={list.isLoading || list.isFetching}
          onChange={onTableChange}
          rowKey={(record) => record.id}
          dataSource={templates?.data || []}
          columns={templateColumns}
          scroll={{ x: 'max-content' }}
          pagination={{
            ...(templates?.pagination as any),
            current: templates?.pagination?.page,
            showSizeChanger: true,
            showTotal: (total, range) => {
              return (
                <StyledTotalLabel>
                  {range[0]}-{range[1]} of {total}
                </StyledTotalLabel>
              );
            }
          }}
        ></Table>
      </WrapperTable>
    </ListLayout>
  );
};
TemplateManagementPage.Index = Index;
TemplateManagementPage.Information = TemplateInformation;
export default TemplateManagementPage;
